import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import _ from "lodash";
import moment from "moment";

Vue.use(Vuex);

export const store = new Vuex.Store({
  state: {
    apiToken: null,
    currentUser: null,
    companies: [],
    branches: [],
    devices: [],
    surveyMode: null,
    survey: {},
    question: {},
    option: {},
    surveyIsEdit: false,
    questionIsEdit: false,
    optionIsEdit: false,
    surveyStats: [],
    landingPages: [
      {
        text: "Ads",
        value: "ads"
      },
      {
        text: "Surveys",
        value: "surveys"
      }
    ],
    userAnswers: [],
    ads: [],
    surveys: [],
    activityCharts: []
  },
  getters: {
    allTime() {
      return {
        fromDate: moment("2018-11-11").format("YYYY-MM-DD"),
        toDate: moment().format("YYYY-MM-DD")
      };
    },
    isUser(state) {
      if (state.currentUser) {
        return state.currentUser.isUser;
      }
      return false;
    },
    isAdmin(state) {
      if (state.currentUser) {
        return state.currentUser.isAdmin;
      }
      return false;
    },
    isSuperAdmin(state) {
      if (state.currentUser) {
        return state.currentUser.isSuperAdmin;
      }
      return false;
    },
    apiToken(state) {
      return state.apiToken;
    },
    currentUser(state) {
      return state.currentUser;
    },
    companies(state) {
      return state.companies;
    },
    branches(state) {
      return state.branches;
    },
    devices(state) {
      return state.devices;
    },
    surveyMode(state) {
      return state.surveyMode;
    },
    survey(state) {
      return state.survey;
    },
    question(state) {
      return state.question;
    },
    option(state) {
      return state.option;
    },
    surveyIsEdit(state) {
      return state.surveyIsEdit;
    },
    questionIsEdit(state) {
      return state.questionIsEdit;
    },
    optionIsEdit(state) {
      return state.optionIsEdit;
    },
    surveyStats(state) {
      return state.surveyStats;
    },
    userAnswers(state) {
      return _.orderBy(state.userAnswers, ["dateAdded"], ["desc"]);
    },
    ads(state) {
      return state.ads;
    },
    activeAds(state, getters) {
      return _.filter(getters.ads, ad => {
        return (
          moment(ad.expiryDate).endOf("day") > moment() ||
          ad.expiryDate === null ||
          ad.expiryDate === undefined
        );
      });
    },
    activeEnabledAds(state, getters) {
      return _.filter(getters.activeAds, ad => {
        return ad.enabled;
      });
    },
    expiredAds(state, getters) {
      return _.filter(getters.ads, ad => {
        return (
          ad.expiryDate !== null &&
          ad.expiryDate !== undefined &&
          moment(ad.expiryDate).endOf("day") < moment()
        );
      });
    },
    surveys(state) {
      return state.surveys;
    },
    activeSurveys(state, getters) {
      return _.filter(getters.surveys, survey => {
        return (
          new Date(survey.expiryDate) > new Date() ||
          survey.expiryDate === null ||
          survey.expiryDate === undefined
        );
      });
    },
    activeEnabledSurveys(state, getters) {
      return _.filter(getters.activeSurveys, survey => {
        return survey.enabled;
      });
    },
    expiredSurveys(state, getters) {
      return _.filter(getters.surveys, survey => {
        return (
          survey.expiryDate !== null &&
          survey.expiryDate !== undefined &&
          new Date(survey.expiryDate) <= new Date()
        );
      });
    },
    activityCharts(state) {
      return state.activityCharts;
    }
  },
  mutations: {
    updateApiToken(state, apiToken) {
      state.apiToken = apiToken;
    },
    updateCurrentUser(state, currentUser) {
      state.currentUser = currentUser;
    },
    updateCompanies(state, companies) {
      state.companies = companies;
    },
    updateBranches(state, branches) {
      state.branches = branches;
    },
    updateDevices(state, devices) {
      state.devices = devices;
    },
    updateSurveyMode(state, surveyMode) {
      state.surveyMode = surveyMode;
    },
    updateSurvey(state, survey) {
      state.survey = survey;
    },
    updateQuestion(state, question) {
      state.question = question;
    },
    updateOption(state, option) {
      state.option = option;
    },
    updateSurveyIsEdit(state, surveyIsEdit) {
      state.surveyIsEdit = surveyIsEdit;
    },
    updateQuestionIsEdit(state, questionIsEdit) {
      state.questionIsEdit = questionIsEdit;
    },
    updateOptionIsEdit(state, optionIsEdit) {
      state.optionIsEdit = optionIsEdit;
    },
    updateSurveyStats(state, surveyStats) {
      state.surveyStats = surveyStats;
    },
    updateUserAnswers(state, userAnswers) {
      state.userAnswers = userAnswers;
    },
    updateAds(state, ads) {
      state.ads = ads;
    },
    updateSurveys(state, surveys) {
      state.surveys = surveys;
    },
    clearManageSurvey(state) {
      state.surveyMode = null;
      state.survey = {};
      state.question = {};
      state.option = {};
      state.surveyIsEdit = false;
      state.questionIsEdit = false;
      state.optionIsEdit = false;
    },
    updateActivityCharts(state, activityCharts) {
      state.activityCharts = activityCharts;
    }
  },
  actions: {
    getCompanies(context) {
      return new Promise((resolve, reject) => {
        axios
          .get(process.env.VUE_APP_API + "/company", {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateCompanies", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getBranches(context) {
      return new Promise((resolve, reject) => {
        axios
          .get(process.env.VUE_APP_API + "/branch", {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateBranches", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getDevices(context) {
      return new Promise((resolve, reject) => {
        axios
          .get(process.env.VUE_APP_API + "/device", {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateDevices", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getSurvey(context, id) {
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${process.env.VUE_APP_API}/survey/${id}?populateChildren=true`,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            context.commit("updateSurvey", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    addSurvey(context, survey) {
      return new Promise((resolve, reject) => {
        axios
          .post(process.env.VUE_APP_API + "/survey", survey, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateSurvey", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    updateSurvey(context, survey) {
      return new Promise((resolve, reject) => {
        axios
          .put(`${process.env.VUE_APP_API}/survey/${survey._id}`, survey, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateSurvey", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    addQuestion(context, question) {
      return new Promise((resolve, reject) => {
        axios
          .post(process.env.VUE_APP_API + "/question", question, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateQuestion", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    updateQuestion(context, question) {
      return new Promise((resolve, reject) => {
        axios
          .put(
            `${process.env.VUE_APP_API}/question/${question._id}`,
            question,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            context.commit("updateQuestion", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    deleteQuestion(context, id) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${process.env.VUE_APP_API}/question/${id}`, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    addOption(context, option) {
      return new Promise((resolve, reject) => {
        axios
          .post(process.env.VUE_APP_API + "/option", option, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateOption", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    updateOption(context, option) {
      return new Promise((resolve, reject) => {
        axios
          .put(`${process.env.VUE_APP_API}/option/${option._id}`, option, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateOption", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    deleteOption(context, id) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${process.env.VUE_APP_API}/option/${id}`, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getSurveyStats(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${process.env.VUE_APP_API}/survey/rpc/get-survey-stats/${payload.companyId}/${payload.id}`,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            let surveys = response.data;
            if (payload.dashboard) {
              surveys = _.filter(surveys, survey => {
                return (
                  survey.enabled &&
                  (new Date(survey.expiryDate) > new Date() ||
                    survey.expiryDate === null ||
                    survey.expiryDate === undefined)
                );
              });
            }
            context.commit("updateSurveyStats", surveys);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    generateMacAddress(context) {
      return new Promise((resolve, reject) => {
        var hexDigits = "0123456789ABCDEF";
        var macAddress = "";
        for (var i = 0; i < 6; i++) {
          macAddress += hexDigits.charAt(Math.round(Math.random() * 15));
          macAddress += hexDigits.charAt(Math.round(Math.random() * 15));
          if (i != 5) macAddress += ":";
        }

        context.dispatch("getUserByMacAddress", macAddress).then(
          result => {
            resolve({ macAddress: macAddress, user: result });
          },
          error => {
            reject(error);
          }
        );
      });
    },
    getUserByMacAddress(context, macAddress) {
      return new Promise((resolve, reject) => {
        axios
          .get(process.env.VUE_APP_API + "/mikrotik/" + macAddress)
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            reject(error);
          });
      });
    },
    getUserAnswers(context, userId) {
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${process.env.VUE_APP_API}/answer?where={"user": "${userId}"}&populate=survey&populate=question&populate=options`
          )
          .then(function(response) {
            context.commit("updateUserAnswers", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            reject(error);
          });
      });
    },
    deleteAnswer(context, id) {
      return new Promise((resolve, reject) => {
        axios
          .delete(`${process.env.VUE_APP_API}/answer/${id}`, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            reject(error);
          });
      });
    },
    getAds(context, where) {
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${process.env.VUE_APP_API}/ad?populate=company&populate=region&populate=user&populate=branch&populate=device${where}`,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            context.commit("updateAds", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getSurveys(context, where) {
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${process.env.VUE_APP_API}/survey?populate=company&populate=region&populate=user&populate=branch&populate=device${where}`,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            context.commit("updateSurveys", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getActivityCharts(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(process.env.VUE_APP_API + "/activity/rpc/summary", payload, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            context.commit("updateActivityCharts", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getBranchReport(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.VUE_APP_API}/reporting/rpc/branch-report`,
            payload,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getAdReport(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(`${process.env.VUE_APP_API}/reporting/rpc/ad-report`, payload, {
            headers: {
              Authorization: "JWT " + context.getters.apiToken
            }
          })
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getSurveyReport(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.VUE_APP_API}/reporting/rpc/survey-report`,
            payload,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getRegionReport(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.VUE_APP_API}/reporting/rpc/region-report`,
            payload,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getUserReport(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.VUE_APP_API}/reporting/rpc/user-report`,
            payload,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getDeviceReport(context, payload) {
      return new Promise((resolve, reject) => {
        axios
          .post(
            `${process.env.VUE_APP_API}/reporting/rpc/device-report`,
            payload,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    },
    getRegions(context, where) {
      return new Promise((resolve, reject) => {
        axios
          .get(
            `${process.env.VUE_APP_API}/region?populate=company&populate=user${where}`,
            {
              headers: {
                Authorization: "JWT " + context.getters.apiToken
              }
            }
          )
          .then(function(response) {
            context.commit("updateAds", response.data);
            resolve(response.data);
          })
          .catch(function(error) {
            console.log(error);
            reject(error);
          });
      });
    }
  }
});
